import React, { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@cargomatic/ui';

import { columnCategoriesMap } from './categories';
import { useTranslation } from 'react-i18next';

const ALL = 'all';

const billingColumnGroups = ['billing_status', 'billing_status_state'];
function getFinanceAndBillingColumns(colDefs) {
  return [
    ...colDefs.filter((col) => billingColumnGroups.includes(col.groupId)),
    ...colDefs.filter((col) =>
      columnCategoriesMap.finance_and_billing.columns.includes(col.colId),
    ),
  ];
}

function getReferenceNumberAndNotesColumns(colDefs) {
  return [
    ...colDefs.filter((col) => col.groupId === 'standard_reference_numbers'),
    ...colDefs.filter((col) =>
      columnCategoriesMap.reference_and_notes.columns.includes(col.colId),
    ),
  ];
}

const basicStopColumnGroups = [
  'loaded_pickup_stops',
  'loaded_delivery_stops',
  'empty_delivery_stops',
  'empty_pickup_stops',
];

const stopColumnsGroups = [
  ...basicStopColumnGroups,
  'offsite_loaded_pickup_stops',
  'offsite_loaded_delivery_stops',
  'offsite_empty_delivery_stops',
  'offsite_empty_pickup_stops',
];

const basicStopColumns = [
  'stop_sequence',
  'stop_address',
  'stop_window_start',
  'stop_status',
  'stop_completed_date',
];

function getBasicStopsInfoColumns(colDefs) {
  const groups = colDefs.filter((colDef) =>
    basicStopColumnGroups.includes(colDef.groupId),
  );

  const basicStopsInfoColumns = groups.map((group) => {
    const children = group.children
      .filter((_, i) => i === 0)
      .map((subGroup) => {
        const columns = basicStopColumns.map(
          (colId) => `${subGroup.groupId}__${colId}`,
        );
        return {
          ...subGroup,
          children: subGroup.children.filter((col) =>
            columns.includes(col.colId),
          ),
        };
      });

    return {
      ...group,
      children,
    };
  }, []);

  return basicStopsInfoColumns;
}

function getStopDetailColumns(colDefs) {
  return colDefs.filter((colDef) => stopColumnsGroups.includes(colDef.groupId));
}

export function ColumnCategoriesDropdown({ api }) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(ALL);

  function handleSelect(category) {
    api?.openToolPanel('columns');
    setSelectedCategory(category);
    const columnsPanel = api?.getToolPanelInstance('columns');

    if (!columnsPanel) {
      return;
    }

    const allColumns = api?.getColumnDefs();

    if (!category || category === ALL) {
      columnsPanel.setColumnLayout(allColumns);
      return;
    }

    if (category === 'finance_and_billing') {
      columnsPanel.setColumnLayout(getFinanceAndBillingColumns(allColumns));
      return;
    }

    if (category === 'reference_and_notes') {
      columnsPanel.setColumnLayout(
        getReferenceNumberAndNotesColumns(allColumns),
      );
      return;
    }

    if (category === 'stop_details') {
      columnsPanel.setColumnLayout(getStopDetailColumns(allColumns));
      return;
    }

    if (category === 'basic_stops_info') {
      columnsPanel.setColumnLayout(getBasicStopsInfoColumns(allColumns));
      return;
    }

    const columnsInSelectedCategory =
      columnCategoriesMap[category]?.columns ?? [];

    if (columnsInSelectedCategory.length > 0) {
      columnsPanel.setColumnLayout(
        allColumns.filter((col) => {
          return columnsInSelectedCategory.includes(col.field);
        }),
      );
    } else {
      columnsPanel.setColumnLayout(
        allColumns.filter((col) => !(col.hide === true && !col.groupId)),
      );
    }
  }
  return (
    <Select value={selectedCategory} onValueChange={handleSelect}>
      <SelectTrigger className="w-[180px]">
        <SelectValue placeholder="Select a category" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>{t('COLUMN_CATEGORIES')}</SelectLabel>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="visible">{t('VISIBLE_COLUMNS')}</SelectItem>
          <SelectItem value="basic_shipment_info">
            {t('BASIC_SHIPMENT_INFO')}
          </SelectItem>
          <SelectItem value="shipment_details">
            {t('SHIPMENT_DETAILS')}
          </SelectItem>
          <SelectItem value="basic_stops_info">
            {t('BASIC_STOPS_INFO')}
          </SelectItem>
          <SelectItem value="stop_details">{t('STOP_DETAILS')}</SelectItem>
          <SelectItem value="dates_and_milestones">
            {t('DATES_AND_MILESTONES')}
          </SelectItem>
          <SelectItem value="finance_and_billing">
            {t('FINANCE_AND_BILLING')}
          </SelectItem>
          <SelectItem value="charges_and_accessorials">
            {t('CHARGES_AND_ACCESSORIALS')}
          </SelectItem>
          <SelectItem value="ocean_tracking">{t('OCEAN_TRACKING')}</SelectItem>
          <SelectItem value="rail_tracking">{t('RAIL_TRACKING')} </SelectItem>
          <SelectItem value="documents">{t('DOCUMENTS')}</SelectItem>
          <SelectItem value="reference_and_notes">
            {t('REFERENCE_AND_NOTES')}
          </SelectItem>
          <SelectItem value="team">{t('TEAM')}</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
